import { type ReactNode } from "react"

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { cn } from "@/lib/utils"

/**
 * OKS-25 — Single quick-stats tile.
 *
 * Per UX Spec §Layout:
 *   - shadcn `Card` shape mirroring the OKS-21 invite-funnel metric
 *     cards (`CardHeader` uppercase label, big `tabular-nums` value)
 *   - `value === null` renders an em-dash (`—`) inside a focusable
 *     `<span tabIndex={0}>`; on focus or hover, a shadcn `Tooltip`
 *     surfaces the empty-state explanation. The tooltip's
 *     `data-testid` lives on the trigger `<span>` (not the wrapper
 *     `Card`) so QA Playwright can `.focus()` it directly.
 *   - `data-value` carries the raw value (or empty string when null)
 *     and `data-unset="true"` flags the empty render — both per QA's
 *     contract ask for stable assertion hooks.
 *
 * The tile itself is decorative-and-textual: `role="group"` +
 * `aria-label={"<label>, <visibleValue>"}` so SRs read both pieces.
 *
 * Locked testids (FE TRD §testid contract, FROZEN by team-lead 2026-05-03):
 *   - tile root  → `data-testid={testId}` (e.g. `crew-list-stat-size`)
 *   - `—` glyph  → `data-testid={tooltipTestId}` when value === null
 */
interface CrewStatTileProps {
  /** Locked tile testid (e.g. `crew-list-stat-size`). */
  testId: string
  /** Locked tooltip-trigger testid (e.g. `crew-list-stat-engagement-tooltip`).
   *  Required iff the tile can render a `—` empty state. */
  tooltipTestId?: string
  /** Localised label rendered uppercase inside `CardHeader`. */
  label: string
  /**
   * Resolved display value for the value slot. Null renders the em-dash
   * + tooltip; non-null callers format their own number (e.g.
   * `5.2%` or `47`) so this component stays presentation-only.
   */
  value: string | null
  /** Localised tooltip body for the empty `—` state. Required when
   *  `value === null`. */
  emptyTooltip?: string
  /**
   * Raw machine-readable value for QA assertion attrs. Mirror of the
   * displayed number when set; empty string when null.
   */
  rawValue?: string | number | null
  className?: string
}

export function CrewStatTile({
  testId,
  tooltipTestId,
  label,
  value,
  emptyTooltip,
  rawValue,
  className,
}: CrewStatTileProps) {
  const isUnset = value === null
  const accessibleValue = isUnset && emptyTooltip ? emptyTooltip : (value ?? "")

  return (
    <Card
      data-testid={testId}
      data-value={
        rawValue === null || rawValue === undefined ? "" : String(rawValue)
      }
      data-unset={isUnset ? "true" : undefined}
      role="group"
      aria-label={`${label}, ${accessibleValue}`}
      className={cn("gap-2", className)}
    >
      <CardHeader className="pb-0">
        <CardTitle className="text-muted-foreground text-xs font-medium tracking-wide uppercase">
          {label}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-foreground text-3xl font-semibold tabular-nums">
          {isUnset ? (
            <EmptyValueWithTooltip
              tooltipTestId={tooltipTestId}
              tooltip={emptyTooltip ?? ""}
            />
          ) : (
            value
          )}
        </p>
      </CardContent>
    </Card>
  )
}

/**
 * The `—` glyph + Tooltip pair. Split out so the `<TooltipProvider>`
 * boundary can be local to the empty state — keeps the populated tile
 * a single `<p>` with no extra subtree.
 *
 * The tooltip mounts on hover OR focus (shadcn `Tooltip` default —
 * Radix `Tooltip.Root`). The trigger `<span>` is `tabIndex={0}` so
 * keyboard users can reach it; Radix automatically wires
 * `aria-describedby` from the trigger to the rendered tooltip
 * content, so SR users hear the explanation exactly once on focus
 * (no `aria-label` on the trigger — that would cause SR to announce
 * the same string twice, per UX review of F5).
 */
function EmptyValueWithTooltip({
  tooltipTestId,
  tooltip,
}: {
  tooltipTestId?: string
  tooltip: string
}): ReactNode {
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          render={
            <span
              tabIndex={0}
              data-testid={tooltipTestId}
              className="focus-visible:ring-ring cursor-help rounded outline-hidden focus-visible:ring-2 focus-visible:ring-offset-2"
            >
              —
            </span>
          }
        />
        <TooltipContent>{tooltip}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}
